import {useMutation} from "@tanstack/react-query";
import {useTranslation} from "react-i18next";
import {toast} from "sonner";

import {TOAST_CONFIG} from "@/lib/constansts";
import {useInvalidate} from "@/src/modules/core/hooks";
import {captureParkingSubmitError} from "@/src/modules/core/lib/glitchtip/parking-capture";

import type {AmenityConfig} from "../lib/amenity-config";
import {getAccessLogService} from "../lib/registry";

type UseAmenityAccessLogUpdateParams = {
  config: AmenityConfig;
  onSuccess?: () => void;
};

export default function useAmenityAccessLogUpdate({
  config,
  onSuccess,
}: UseAmenityAccessLogUpdateParams) {
  const {t} = useTranslation("properties");
  const service = getAccessLogService(config);
  const invalidateSingular = useInvalidate(config.accessLogPath);
  const invalidatePlural = useInvalidate(config.accessLogsSectionKey);

  return useMutation({
    mutationFn: (data: Record<string, unknown>) => {
      const id = data.amenity_access_log_id as number;
      return service.updateAccessLog(id, data);
    },
    retry: false,
    onSuccess: () => {
      invalidateSingular();
      invalidatePlural();
      toast.success(t("features.amenity.toast.access_log_updated"), TOAST_CONFIG);
      onSuccess?.();
    },
    onError: (error) => {
      captureParkingSubmitError(error, {
        feature: config.guestRuleFeature,
        operation: `amenity_access_log_${config.type}_update`,
      });
    },
  });
}
